// Copyright 2016 Sorama B.V.

import { computedFrom } from "aurelia-framework";

/**
 * Status code enum representing a three-way 'boolean'
 */
export enum StatusCode {
    // Positive action-result aka TRUE
    Available = 2,

    // Transition state, eg. an action is in progress to transit from the Nnegative state to the positive state
    InProgress = 1,

    // Negative action result aka FALSE
    NotAvailable = 0,
}

/**
 * Represents a three-way state for an action which can be 'Available', 'InProgress', 'NotAvailable'
 */
export class Status {
    private _statusCode: StatusCode;
    private _reason: string;

    /**
     * Constructor is for internal use only, please use static factory methods instead (create...)
     * @param statusCode
     * @param reason
     */
    constructor(statusCode: StatusCode, reason?: string) {
        this._statusCode = statusCode;
        this._reason = reason;
    }

    /**
     * Get the internal status code, see 'StatusCode'. Any non-negative state has a non-zero value so this value can safely be used for checking if the state is not 'NotAvailable'.
     */
    @computedFrom("_statusCode")
    public get statusCode(): StatusCode {
        return this._statusCode;
    }

    /**
     * Returns true if the internal state is 'Available' or false if not
     */
    @computedFrom("_statusCode")
    public get available(): boolean {
        return this._statusCode == StatusCode.Available;
    }

    /**
     * Returns true if the internal state is 'InProgress' or false if not
     */
    @computedFrom("_statusCode")
    public get inProgress(): boolean {
        return this._statusCode == StatusCode.InProgress;
    }

    /**
     * Returns true if the internal state is 'NotAvailable' or false if not
     */
    @computedFrom("_statusCode")
    public get notAvailable(): boolean {
        return this._statusCode == StatusCode.NotAvailable;
    }

    /**
     * Gets a consise string containing information about why the state is the current state. If there is no information available, this property will be undefined.
     * The 'Available' state typically does not provide a reason.
     */
    @computedFrom("_reason")
    public get reason(): string {
        return this._reason;
    }

    /**
     * Creates a new 'Status' object with internal state Available
     */
    public static createAvailable(): Status {
        return new Status(StatusCode.Available);
    }

    /**
     * Create a new 'Status' object with internal state 'InProgress' and optionally a reason
     * @param reason A consise string describing why the action is in progress. NOTE: If there is no extraordinary reason
     * for being in progress this message should be ommitted to keep the ui clearer.
     * Example: Reserving a device is a normal step and does not warrent an additional reason string,
     * however if there is some issue causing the reservation to take a longer time this should be communicated.
     */
    public static createInProgress(reason?: string): Status {
        return new Status(StatusCode.InProgress, reason ? reason : undefined);
    }

    /**
     * Create a new 'Status' object with internal state 'NotAvailable' and optionally a reason
     * @param reason A consise string describing why the action is in progress. NOTE: If there is no extraordinary reason
     * for being in progress this message should be ommitted to keep the ui clearer.
     * Example: A status of 'NotAvailable' because the user has not selected a device is a normal situation and does not require additional explanation
     * however if there is some error causing the device to be unavailable should be communicated via the reason string.
     */
    public static createNotAvailable(reason?: string): Status {
        return new Status(StatusCode.NotAvailable, reason ? reason : undefined);
    }
}

export class StatusItem<TObject> {
    public status: Status;

    public item?: TObject;
}
